import { Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { LayoutGroup, motion } from 'motion/react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { get, photoUrl, post } from '../lib/api.ts';
import { hasStaffAccess, roleLabel, setView, useMe, type Me } from '../lib/session.ts';
import { time } from '../lib/format.ts';
import { BrandLockup } from './Brand.tsx';
import { Icon, type IconName } from './Icon.tsx';
import { Avatar, Button, Segmented } from './ui.tsx';
import { CommandPalette } from './CommandPalette.tsx';
import { Notifications } from './Notifications.tsx';
import { DevicePrefs, PushPrompt } from './PushPrompt.tsx';
import { ChangePasswordDialog } from './ChangePassword.tsx';
import { forgetPushDevice } from '../lib/push.ts';

interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  show: (m: Me) => boolean;
  badge?: number;
  exact?: boolean;
}

function useNav(me: Me) {
  const inbox = useQuery({ queryKey: ['inbox-count'], queryFn: () => get<{ leave: number; overtime: number; corrections: number }>('/api/requests/inbox/count'), enabled: me.access.approvals, refetchInterval: 60_000 });
  const pending = inbox.data ? inbox.data.leave + inbox.data.overtime + inbox.data.corrections : 0;
  return useMemo(
    () =>
      [
        {
          label: 'Today',
          items: [
            { to: '/', label: me.access.people ? 'Overview' : 'My day', icon: 'today', show: () => true, exact: true },
            { to: '/check-in', label: 'Check in', icon: 'face', show: (m: Me) => !!m.employee },
            { to: '/approvals', label: 'Approvals', icon: 'inbox', show: (m: Me) => m.access.approvals, badge: pending },
          ],
        },
        {
          label: 'People',
          items: [
            { to: '/people', label: 'Directory', icon: 'people', show: (m: Me) => m.access.people },
            { to: '/org', label: 'Organisation', icon: 'org', show: (m: Me) => m.access.peopleAll },
            { to: '/me', label: 'My profile', icon: 'user', show: (m: Me) => !!m.employee },
          ],
        },
        {
          label: 'Time',
          items: [
            { to: '/attendance', label: 'Live board', icon: 'clock', show: (m: Me) => m.access.attendance },
            { to: '/roster', label: 'Roster', icon: 'roster', show: (m: Me) => m.access.attendance },
            { to: '/time', label: 'My time & leave', icon: 'roster', show: (m: Me) => !!m.employee && !m.access.attendance },
          ],
        },
        {
          label: 'Pay',
          items: [
            { to: '/payroll', label: 'Payroll runs', icon: 'wallet', show: (m: Me) => m.access.payroll },
            { to: '/payroll/components', label: 'Components', icon: 'formula', show: (m: Me) => m.access.payroll },
            { to: '/payroll/simulate', label: 'Salary simulation', icon: 'spark', show: (m: Me) => m.access.payroll },
            { to: '/payroll/goods', label: 'Goods invoices', icon: 'box', show: (m: Me) => m.access.goodsPurchase },
            { to: '/payslips', label: 'My payslips', icon: 'file', show: (m: Me) => !!m.employee },
          ],
        },
        {
          label: 'Workplace',
          items: [
            { to: '/devices', label: 'Devices', icon: 'fingerprint', show: (m: Me) => m.access.deviceUsers },
            { to: '/canteen', label: 'Canteen', icon: 'canteen', show: (m: Me) => m.access.canteen },
            { to: '/cooperative', label: 'Cooperative', icon: 'coins', show: (m: Me) => m.access.cooperative || !!m.employee },
            { to: '/company', label: 'Company', icon: 'building', show: (m: Me) => m.access.master && (m.access.masterEdit || m.access.peopleAll) },
          ],
        },
        {
          label: 'System',
          items: [
            { to: '/settings', label: 'Settings', icon: 'sliders', show: (m: Me) => m.access.settings },
            { to: '/access', label: 'Access', icon: 'shield', show: (m: Me) => m.access.access },
            { to: '/developer', label: 'Apps & sign-in', icon: 'key', show: (m: Me) => m.access.developer },
            { to: '/audit', label: 'Audit trail', icon: 'scroll', show: (m: Me) => m.access.developer || m.access.settings },
          ],
        },
      ]
        .map((g) => ({ ...g, items: (g.items as NavItem[]).filter((i) => i.show(me)) }))
        .filter((g) => g.items.length),
    [me, pending],
  );
}

function OfficeClock({ tz, name }: { tz: string; name: string }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 15_000);
    return () => clearInterval(t);
  }, []);
  const abbr = tz === 'Asia/Makassar' ? 'WITA' : tz === 'Asia/Jakarta' ? 'WIB' : tz === 'Asia/Jayapura' ? 'WIT' : '';
  return (
    <div className="office-switch" title={tz}>
      <Icon name="building" width={16} height={16} />
      <span>{name}</span>
      <span className="office-switch__time">
        {time(now, tz)} {abbr}
      </span>
    </div>
  );
}

export function AppShell({ me }: { me: Me }) {
  const groups = useNav(me);
  const path = useRouterState({ select: (s) => s.location.pathname });
  const [navOpen, setNavOpen] = useState(false);
  const [cmdOpen, setCmdOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [theme, setTheme] = useState(() => document.documentElement.dataset.theme ?? 'dark');

  useEffect(() => setNavOpen(false), [path]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setCmdOpen((v) => !v);
      }
      if (e.key === 'Escape') setNavOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    document.documentElement.dataset.theme = next;
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', next === 'dark' ? '#12161c' : '#f5f6f8');
    try {
      localStorage.setItem('hearth.theme', next);
    } catch {
      /* private mode */
    }
    setTheme(next);
  };

  const signOut = async () => {
    await forgetPushDevice();
    try {
      await post('/api/auth/logout');
    } finally {
      // Leave even if the server call fails, or the button looks dead.
      setView('staff');
      qc.clear();
      navigate({ to: '/login' });
    }
  };

  const canSwitchView = !!me.employee && (me.viewingAsEmployee || hasStaffAccess(me));
  const switchView = (view: 'staff' | 'personal') => {
    setView(view);
    void navigate({ to: '/' });
  };

  const isActive = (i: NavItem) => (i.exact ? path === i.to : path === i.to || path.startsWith(`${i.to}/`));
  const active = groups.flatMap((g) => g.items).filter(isActive).sort((a, b) => b.to.length - a.to.length)[0];

  return (
    <div className="shell" data-nav-open={navOpen}>
      <aside className="rail" aria-label="Main navigation">
        <Link to="/" className="brand">
          <BrandLockup className="brand__lockup" title="GlobalXtreme Hearth — home" />
        </Link>
        {me.employee?.timeZone ? <OfficeClock tz={me.employee.timeZone} name={me.employee.officeName ?? 'Office'} /> : null}
        {canSwitchView ? (
          <div className="view-switch" title="My view shows Hearth exactly as a regular employee sees it">
            <Segmented label="View" idPrefix="view" value={me.viewingAsEmployee ? 'personal' : 'staff'} onChange={switchView} options={[{ id: 'staff', label: 'HR view' }, { id: 'personal', label: 'My view' }]} />
          </div>
        ) : null}
        <LayoutGroup id="nav">
          <nav className="nav">
            {groups.map((g) => (
              <div key={g.label} className="nav__group" role="group" aria-labelledby={`nav-${g.label}`}>
                <div className="nav__group-label" id={`nav-${g.label}`}>
                  {g.label}
                </div>
                {g.items.map((i) => (
                  <Link key={i.to} to={i.to} className="nav__item" data-active={active?.to === i.to} aria-current={active?.to === i.to ? 'page' : undefined}>
                    {active?.to === i.to ? <motion.span layoutId="nav-tick" className="nav__tick" transition={{ type: 'spring', stiffness: 480, damping: 38 }} /> : null}
                    <Icon name={i.icon} />
                    <span>{i.label}</span>
                    {i.badge ? <span className="nav__badge">{i.badge}</span> : null}
                  </Link>
                ))}
              </div>
            ))}
          </nav>
        </LayoutGroup>
        <div className="rail__foot">
          <DevicePrefs me={me} />
          <div className="me-card">
            <Avatar name={me.displayName} size={32} photo={me.employee ? photoUrl(me.employee.id) : null} />
            <div className="me-card__text">
              <div className="me-card__name">{me.displayName}</div>
              <div className="me-card__role">{roleLabel(me)}</div>
            </div>
            <div className="me-card__actions">
              <Button variant="ghost" size="sm" iconOnly icon={theme === 'dark' ? 'sun' : 'moon'} onClick={toggleTheme} aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`} title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`} />
              <Button variant="ghost" size="sm" iconOnly icon="key" onClick={() => setPasswordOpen(true)} aria-label="Change password" title="Change password" />
              <Button variant="ghost" size="sm" iconOnly icon="logout" onClick={signOut} aria-label="Sign out" title="Sign out" />
            </div>
          </div>
          <ChangePasswordDialog open={passwordOpen} onClose={() => setPasswordOpen(false)} />
        </div>
      </aside>
      {navOpen ? <div className="scrim" style={{ zIndex: 55 }} onClick={() => setNavOpen(false)} /> : null}
      <main className="main" id="main">
        <div className="topbar">
          <Button variant="ghost" iconOnly icon="menu" className="mobile-bar" aria-label="Open navigation" onClick={() => setNavOpen(true)} />
          <Crumbs path={path} label={active?.label} />
          {me.viewingAsEmployee ? (
            <button type="button" className="view-banner" onClick={() => switchView('staff')}>
              <Icon name="eye" width={14} height={14} />
              <span className="view-banner__lead">Seeing Hearth as an employee · </span>
              <u>back to HR view</u>
            </button>
          ) : null}
          <div className="topbar__spacer" />
          <button className="command-trigger" onClick={() => setCmdOpen(true)} aria-label="Search people and pages">
            <Icon name="search" />
            <span>Search people, pages…</span>
            <kbd>⌘K</kbd>
          </button>
          <Notifications />
        </div>
        <div className="content">
          <Outlet />
        </div>
      </main>
      <CommandPalette open={cmdOpen} onClose={() => setCmdOpen(false)} groups={groups} me={me} />
      <PushPrompt me={me} />
    </div>
  );
}

function Crumbs({ path, label }: { path: string; label?: string }): ReactNode {
  const parts = path.split('/').filter(Boolean);
  return (
    <nav className="topbar__crumbs" aria-label="Breadcrumb">
      <Link to="/">Hearth</Link>
      {label ? (
        <>
          <Icon name="chevronRight" width={12} height={12} />
          <span aria-current={parts.length <= 1 ? 'page' : undefined}>{label}</span>
        </>
      ) : null}
      {parts.length > 1 && parts[0] !== 'payroll' ? (
        <>
          <Icon name="chevronRight" width={12} height={12} />
          <span aria-current="page" className="mono">
            {parts.at(-1)!.length > 12 ? `${parts.at(-1)!.slice(0, 8)}…` : parts.at(-1)}
          </span>
        </>
      ) : null}
    </nav>
  );
}
